import clsx from 'clsx'
import map from 'lodash/map'
import React from 'react'

import { Loading } from '@Components/Modal/Loading'
import { ShippingModalUrgencyTimer } from '@Components/Shipping/ShippingPricing/UrgencyTimer'
import { ShippingInfoQuery, useShippingInfoQuery } from '@Generated/graphql'
import { MetadataContext } from '@Globals/Metadata'
import { useCheapestShippingPrice, useFreeShippingThreshold } from '@Globals/Metadata/Hooks'
import { useTranslation } from '@Globals/Translations'

import styles from './ShippingPricingV1.module.scss'

type ShippingInfoType = NonNullable<ShippingInfoQuery['shippingInfo']>
type ShippingInfoOption = NonNullable<ShippingInfoType['items']>[0]

export const ShippingPricingV1 = ({
  id,
  showFreeShippingDescription,
}: {
  id?: string
  showFreeShippingDescription: boolean
}) => {
  const { store } = React.useContext(MetadataContext)
  const { data, error, loading } = useShippingInfoQuery({
    context: {
      domainProxy: true,
      userSpecific: true,
    },
  })
  const isLoading = (!store || loading) && !error
  const { t, tDanger } = useTranslation()
  const cheapestShippingPrice = useCheapestShippingPrice()
  const freeShippingThreshold = useFreeShippingThreshold()
  const isGlobalShippingFree = cheapestShippingPrice === null || cheapestShippingPrice?.value === 0
  const shippingOptions: ShippingInfoOption[] = data?.shippingInfo?.items || []

  if (isLoading) { return <div className={styles.loading}><Loading /></div> }

  return (
    <>
      <div className={clsx(styles.banner, 'fullwidth')} id={id}>
        {isGlobalShippingFree && <div className={styles.shippingDescription}>{t('header:FREE_STANDARD_SHIPPING_ALL_ORDERS')}</div>}
        {!isGlobalShippingFree && freeShippingThreshold && showFreeShippingDescription && (
          <div
            {...tDanger(
              'header:DELIVERY_INFO_INTRO_TEXT_WITH_THRESHOLD',
              { arg1: freeShippingThreshold.format },
            )}
            className={styles.shippingDescription}
          />
        )}
        <ShippingModalUrgencyTimer className={styles.urgency} />
      </div>

      <div className={clsx(styles.shippingPriceGrid, freeShippingThreshold && styles.hasFreeShippingThreshold)}>
        <p className={clsx(styles.header)}>{t('common:DELIVERY_LABEL')}</p>
        <p className={clsx(styles.header)}>{t('common:CART_PRODUCT_LIST_PRICE_LABEL', 'price')}</p>
        {(freeShippingThreshold || isGlobalShippingFree) && (
          <p
            className={clsx(styles.header)}
            {...tDanger(
              'shipping:ORDERS_OVER_X_THRESHOLD',
              { arg1: freeShippingThreshold?.format },
            )}
          />
        )}
        {map(
          shippingOptions,
          ((option: ShippingInfoOption, i) => (
            <React.Fragment key={`${option.title}_${option.deliveryTime?.text}_${option.price?.value?.format}${i}`}>
              <div key={`${option.title}_${option.deliveryTime?.text}_${option.price?.value?.format}1`} className={styles.shippingOption}>
                <img
                  alt="es"
                  className={styles.shippingLogo}
                  loading="lazy"
                  src={option.logo?.url}
                />
                <p className={styles.deliveryType}>{option.title}</p>
                {option.deliveryTime && <p className={styles.deliveryTime}>{option.deliveryTime.text}</p>}
              </div>

              <div key={`${option.title}_${option.deliveryTime?.text}_${option.price?.value?.format}2`} className={styles.price}>
                {option.priceBefore?.value?.format && <span className={styles.shippingBeforePrice}>{option.priceBefore?.value?.format}</span>}
                <span>
                  {option.price?.text
                    ? <strong>{option.price?.text}</strong>
                    : option.price?.value?.format}
                </span>
              </div>

              {(freeShippingThreshold || isGlobalShippingFree) && (
                <div key={`${option.title}_${option.deliveryTime?.text}_${option.price?.value?.format}3`} className={styles.afterThreshold}>
                  {option.priceAboveThreshold?.text
                    ? <strong>{option.priceAboveThreshold?.text}</strong>
                    : option.priceAboveThreshold?.value?.format}
                </div>
              )}

            </React.Fragment>
          )),
        )}
      </div>
    </>

  )
}
